import React, { ChangeEvent, useCallback } from 'react';
import styled from 'styled-components';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';

export const calculateTextareaHeight = (
  textLength?: number,
  containerWidth?: number,
): number | undefined => {
  if (!textLength || !containerWidth) return undefined;
  const fontSize = 13; // Font size in pixels
  const lineHeight = fontSize * 1; // Assuming a line-height of 1
  const charWidth = fontSize * 0.5; // Approximate character width in pixels (half of the font size)
  const paddingTop = 8; // Padding top in pixels
  const paddingBottom = 8; // Padding bottom in pixels
  const regularTextareaHeight = 53; // assuming the default textarea height

  // Calculate the number of characters per line
  const charsPerLine = Math.floor(containerWidth / charWidth);

  // Calculate the number of lines needed
  const lines = Math.ceil(textLength / charsPerLine);

  // Calculate the height of the textarea
  const height = lines * lineHeight + paddingTop + paddingBottom;

  return height > regularTextareaHeight
    ? height + charsPerLine + paddingTop + paddingBottom
    : height;
};

type TrueImpactTextfieldPropsBase = {
  id?: string;
  value?: string | number;
  defaultValue?: string | number;
  type?: string;
  disabled?: boolean;
  name?: string;
  title?: string;
  className?: string;
  autoFocus?: boolean;
  placeholder: string;
  backgroundColor?: keyof typeof COLORS;
  placeholderColor?: keyof typeof COLORS;
  isError?: boolean;
  isWarning?: boolean;
  maxLength?: number;
  onBlur?: () => unknown;
  onFocus?: () => unknown;
  onEnterPress?: () => unknown;
  onChange?: (
    value: string,
    name?: string,
    e?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => unknown;
};

export type TrueImpactTextfieldProps = TrueImpactTextfieldPropsBase &
  (
    | {
        isTextarea: true;
        height?: number;
        inputRef?: React.RefObject<HTMLTextAreaElement> | null;
      }
    | {
        isTextarea?: false;
        height?: undefined;
        inputRef?: React.RefObject<HTMLInputElement> | null;
      }
  );

const StyledInput = styled.input<{
  isError?: boolean;
  isWarning?: boolean;
  backgroundColor?: keyof typeof COLORS;
  placeholderColor: keyof typeof COLORS;
}>`
  width: 100%;
  padding: 14px;
  font-size: 16px;
  border-radius: ${SPACERS.extraTiny};
  border: 1px solid
    ${({ isError, isWarning }) =>
      isError ? COLORS.critical : isWarning ? COLORS.warning : COLORS.grayBase};
  ${({ backgroundColor }) =>
    backgroundColor ? `background-color: ${COLORS[backgroundColor]};` : ''}
  &:disabled {
    cursor: not-allowed;
    color: ${COLORS.grayBase} !important;
  }
  ::placeholder {
    color: ${({ placeholderColor }) => COLORS[placeholderColor]};
    opacity: 1;
  }
  ::-ms-input-placeholder {
    color: ${({ placeholderColor }) => COLORS[placeholderColor]};
  }
`;

const StyledTextarea = styled.textarea<{
  isError?: boolean;
  isWarning?: boolean;
  minHeight?: number;
  backgroundColor?: keyof typeof COLORS;
  placeholderColor: keyof typeof COLORS;
}>`
  width: 100%;
  padding: 14px;
  ${({ minHeight }) => (minHeight ? `min-height: ${minHeight}px;` : '')}
  font-size: 16px;
  resize: vertical;
  border-radius: ${SPACERS.extraTiny};
  border: 1px solid
    ${({ isError, isWarning }) =>
      isError ? COLORS.critical : isWarning ? COLORS.warning : COLORS.grayBase};
  background-color: ${({ backgroundColor }) =>
    backgroundColor ? COLORS[backgroundColor] : COLORS.grayLightest};
  ::placeholder {
    color: ${({ placeholderColor }) => COLORS[placeholderColor]};
    opacity: 1;
  }

  ::-ms-input-placeholder {
    color: ${({ placeholderColor }) => COLORS[placeholderColor]};
  }
`;

const TrueImpactTextfield: React.FC<TrueImpactTextfieldProps> = ({
  id,
  type,
  name,
  title,
  height,
  value,
  disabled,
  inputRef,
  autoFocus,
  className,
  isTextarea,
  isError,
  isWarning,
  placeholder,
  backgroundColor,
  placeholderColor = 'grayBase',
  defaultValue,
  maxLength,
  onFocus,
  onBlur,
  onChange,
  onEnterPress,
}) => {
  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (onEnterPress && e.key === 'Enter') {
        onEnterPress();
      }
    },
    [onEnterPress],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      onChange?.(value, name, e);
    },
    [onChange, name],
  );

  if (isTextarea) {
    return (
      <StyledTextarea
        id={id}
        minHeight={height}
        ref={inputRef}
        name={name}
        title={title}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        className={className}
        placeholder={placeholder}
        defaultValue={defaultValue}
        isError={isError}
        isWarning={isWarning}
        backgroundColor={backgroundColor}
        placeholderColor={placeholderColor}
        maxLength={maxLength}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyUp={handleKeyUp}
        onChange={handleChange}
      />
    );
  }

  return (
    <StyledInput
      id={id}
      name={name}
      ref={inputRef}
      type={type}
      title={title}
      alt={title}
      value={value}
      disabled={disabled}
      autoFocus={autoFocus}
      className={className}
      placeholder={placeholder}
      defaultValue={defaultValue}
      isError={isError}
      isWarning={isWarning}
      backgroundColor={backgroundColor}
      placeholderColor={placeholderColor}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyUp={handleKeyUp}
      onChange={handleChange}
    />
  );
};

export default TrueImpactTextfield;
